import React, { useState } from 'react';
import FilterTemplate from '../components/filtertemplate/FilterTemplate';
import CustomSelect from '../components/shared/CustomSelect';
import { useUser } from '../utils/UserContext';
import FilterChips from '../components/shared/FilterChips';
import HelpButton from '../components/shared/HelpButton';
import { CalendarIcon, UserIcon, CloseIcon, DocumentIcon, UploadIcon } from '../components/shared/Icons';
import { cssUrl } from '../utils/cssVersion';
import { fetchLeave, handleLeaveSubmit, downloadRequestLeave } from '../utils/api';
import { rolesAccess } from '../data/map';
import { CHIP_GROUP_COLORS } from '../components/shared/chipColors';

const filterLeaveStyles = cssUrl('filterTemplate.css');

// Row index of "Time Worked" in the /api/leave rows returned by fetchLeave.
// Adjust to match your get_leave() column order.
const TIME_WORKED_IDX = 11;

// Leave types that use the Time Worked field (same list as NewRequestLeave)
const TIME_WORKED_LEAVE = ['Sick Leave', 'Carers Leave'];

const fmt2 = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? (v ?? '—') : n.toFixed(2);
};

const displayDate = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

// ===== HELPER: keep only a non-negative decimal (digits + at most one dot) =====
const sanitiseDecimal = (raw) => {
  let s = String(raw ?? '').replace(/[^0-9.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  return s;
};

// ===== HELPER: Get last day of a month =====
const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// ===== HELPER: Which pay half does a day fall in? 1 = 1st–15th, 2 = 16th–end =====
const getPayHalf = (dayOfMonth) => (dayOfMonth <= 15 ? 1 : 2);

// ===== HELPER: Is a YYYY-MM-DD string in a prior month relative to today? =====
const isPriorMonth = (dateStr) => {
  if (!dateStr) return false;
  const [y, m] = dateStr.split('-').map(Number);
  const now = new Date();
  return y < now.getFullYear() || (y === now.getFullYear() && m - 1 < now.getMonth());
};

// ===== HELPER: Clamp a date to the same pay-half as the anchor date =====
// Returns { clamped: 'YYYY-MM-DD', didClamp: bool, boundaryDay: number, overflowDay: number, lastDay: number }
const clampToPayHalf = (anchorStr, targetStr) => {
  if (!anchorStr || !targetStr) return { clamped: targetStr, didClamp: false };
  const [ay, am, ad] = anchorStr.split('-').map(Number);
  const [ty, tm, td] = targetStr.split('-').map(Number);

  // Different month — clamping doesn't apply here; same-month is enforced separately
  if (ay !== ty || am !== tm) return { clamped: targetStr, didClamp: false };

  const anchorHalf = getPayHalf(ad);
  const targetHalf = getPayHalf(td);
  if (anchorHalf === targetHalf) return { clamped: targetStr, didClamp: false };

  const lastDay = getLastDayOfMonth(ay, am - 1);
  const boundaryDay = anchorHalf === 1 ? 15 : 16;
  const pad = (n) => String(n).padStart(2, '0');
  const clamped = `${ay}-${pad(am)}-${pad(boundaryDay)}`;
  return { clamped, didClamp: true, boundaryDay, overflowDay: td, lastDay };
};


const LeaveCard = ({ item, onClick, isAdmin }) => (
  <div className="fr-card" onClick={() => onClick(item)}>
    <div className="fr-card-header">
      <div className="fr-card-dates">
        <CalendarIcon />
        <span className="fr-card-date-start">{displayDate(item[2])}</span>
        <span className="fr-card-date-sep">→</span>
        <span className="fr-card-date-end">{displayDate(item[3])}</span>
      </div>
      <div className="fr-card-status-badge" data-status={item[5]?.toLowerCase()}>
        {item[5]}
      </div>
    </div>

    <div className="fr-card-body">
      <div className="fr-card-row">
        <UserIcon />
        <span className="fr-card-label">Employee:</span>
        <span className="fr-card-value">{item[1]}</span>
      </div>

      <div className="fr-card-row">
        <CalendarIcon />
        <span className="fr-card-label">Leave Type:</span>
        <span className="fr-card-value">{item[4]}</span>
      </div>
      <div className="fr-card-row">
        <DocumentIcon />
        <span className="fr-card-label">Document:</span>
        <span className="fr-card-value">{item[9]}</span>
        <span className="fr-card-status-tag" data-status={item[10]?.toLowerCase()}>
          {item[10]==="Once" ? "Monthly" : "Bi-Monthly"}
        </span>
      </div>
    </div>

  <div className="fr-card-footer">
      <div className="fr-card-meta">
        <span className="fr-card-meta-item">
          <CalendarIcon />
          Weekdays: {fmt2(item[7])} &nbsp; Weekend: {fmt2(item[8])} &nbsp; Employed By: {item[6]}
        </span>
      </div>

    </div>

    <div className="fr-card-tap-hint">Tap to update leave doc</div>
  </div>
);


const LeaveModal = ({ item, onClose, onSubmit, isSubmitting, onToast, isAdmin, user }) => {

    const [isDragging, setIsDragging] = useState(false);
    const [requestDate, setRequestDate] = useState(item?.[2] || '');
    const [returnDate, setReturnDate] = useState(item?.[3] || '');
    const [selectedFile, setSelectedFile] = useState(null);
    const [leaveType, setLeaveType] = useState(item?.[4]?.toString() || '');
    const [statusApproved, setStatusApproved] = useState(item?.[5]?.toString() || '');
    const [timeWorked, setTimeWorked] = useState(sanitiseDecimal(item?.[TIME_WORKED_IDX] ?? '0') || '0');
    const typeLeaves = ['Holidays', 'Sick Leave', 'Carers Leave', 'Other Leave', 'Compassionate', 'Work Cover', 'Termination','Cancelled'];
    const leaveApproved = ['Approved', 'Not Approved', 'Cancelled by Employee', 'Pending', 'Approved Without Pay'];

    const isBiMonthly = item?.[10] === 'Twice';
    const showTimeWorked = TIME_WORKED_LEAVE.includes(leaveType);

    
    
    const recordIsPriorMonth = isPriorMonth(item?.[2]) || isPriorMonth(item?.[3]);
    const datesReadOnly = !isAdmin && recordIsPriorMonth;

    const handleDrag = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "dragenter" || e.type === "dragover") setIsDragging(true);
      else if (e.type === "dragleave") setIsDragging(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        setSelectedFile(e.dataTransfer.files[0]);
      }
    };

    
    const handleLeaveTypeChange = (val) => {
      setLeaveType(val);
      if (!TIME_WORKED_LEAVE.includes(val)) setTimeWorked('0');
    };

   
    const handleTimeWorkedChange = (e) => {
      setTimeWorked(sanitiseDecimal(e.target.value));
    };

    const handleRequestDateChange = (val) => {
      if (!val) {
        setRequestDate('');
        return;
      }

     
      if (!isAdmin && isPriorMonth(val)) {
        onToast({ message: "Request date can't be in a prior month.", type: 'warning' });
        setRequestDate('');
        setReturnDate('');
        return;
      }

      
      setRequestDate(val);
      setReturnDate(val);
    };

    const handleReturnDateChange = (val) => {
      if (!val) {
        setReturnDate('');
        return;
      }

     
      if (!isAdmin && isPriorMonth(val)) {
        onToast({ message: "Return date can't be in a prior month.", type: 'warning' });
        setRequestDate('');
        setReturnDate('');
        return;
      }

      let startVal = requestDate;
      let endVal = val;

      
      if (!startVal) {
        setRequestDate(endVal);
        setReturnDate(endVal);
        return;
      }

     
      if (endVal < startVal) {
        onToast({
          message: 'Return date cannot be before request date. Change the request date to shift the range.',
          type: 'warning',
        });
        return;
      }

      // Ceiling: end must stay in the same month as start.
      const [sy, sm] = startVal.split('-').map(Number);
      const [ey, em] = endVal.split('-').map(Number);
      if (sy !== ey || sm !== em) {
        const lastDay = getLastDayOfMonth(sy, sm - 1);
        const pad = (n) => String(n).padStart(2, '0');
        endVal = `${sy}-${pad(sm)}-${pad(lastDay)}`;
        onToast({
          message: `Return date must stay within the same month. Clamped to ${lastDay}th.`,
          type: 'warning',
        });
      }

      // Bi-Monthly ceiling: end must stay in start's pay-half.
      if (isBiMonthly) {
        const clampResult = clampToPayHalf(startVal, endVal);
        if (clampResult.didClamp) {
          const { boundaryDay, overflowDay, lastDay } = clampResult;
          endVal = clampResult.clamped;
          const remainStart = boundaryDay === 15 ? 16 : 1;
          const remainEnd = boundaryDay === 15 ? lastDay : 15;
          onToast({
            message: `Bi-Monthly: return clamped to ${boundaryDay}th. Create a separate request for ${remainStart}–${remainEnd} to cover up to the ${overflowDay}th.`,
            type: 'warning',
          });
        }
      }

      setReturnDate(endVal);
    };

    // Final sanity check on submit — guards against any edge case
    const validateOnSubmit = () => {
      if (!requestDate || !returnDate) {
        onToast({ message: 'Both dates are required.', type: 'warning' });
        return false;
      }

      if (!leaveType) {
        onToast({ message: 'Type of leave is required.', type: 'warning' });
        return false;
      }

      // Time Worked must be a valid non-negative decimal
      const tw = timeWorked.trim();
      const twNum = parseFloat(tw);
      if (tw === '' || tw === '.' || Number.isNaN(twNum) || twNum < 0) {
        onToast({ message: 'Time worked must be a number of hours (0 or more).', type: 'warning' });
        return false;
      }

      // Non-admin: no prior-month dates allowed on submit (defence in depth)
      if (!isAdmin) {
        if (isPriorMonth(requestDate) || isPriorMonth(returnDate)) {
          // If the record was already prior-month, the fields should be read-only
          // and the original values are preserved — let those through. Otherwise block.
          if (!recordIsPriorMonth) {
            onToast({ message: "Dates can't be in a prior month.", type: 'warning' });
            return false;
          }
        }
      }

      // Same month
      const [sy, sm] = requestDate.split('-').map(Number);
      const [ey, em] = returnDate.split('-').map(Number);
      if (sy !== ey || sm !== em) {
        onToast({ message: 'Request and return date must be in the same month.', type: 'warning' });
        return false;
      }

      // Return on or after request
      if (returnDate < requestDate) {
        onToast({ message: 'Return date must be on or after the request date.', type: 'warning' });
        return false;
      }

      // Bi-monthly half
      if (isBiMonthly) {
        const [, , sd] = requestDate.split('-').map(Number);
        const [, , ed] = returnDate.split('-').map(Number);
        if (getPayHalf(sd) !== getPayHalf(ed)) {
          if (sd <= 15) {
            onToast({ message: 'Both dates must be on or before the 15th for this pay period.', type: 'warning' });
          } else {
            onToast({ message: 'Both dates must be between the 16th and end of month for this pay period.', type: 'warning' });
          }
          return false;
        }
      }

      return true;
    };

    const handleSubmit = () => {
      if (!validateOnSubmit()) return;
      onSubmit({
        recordId: item[0],
        employee: item[1],
        request: requestDate,
        return: returnDate,
        type_leave: leaveType,
        status_approval: statusApproved,
        time_worked: String(parseFloat(timeWorked)),   // normalised decimal, e.g. "2.5"
        myFile: selectedFile,
      });
    };

  return (
    <div className="fr-modal-overlay" onClick={onClose}>
      <div className="fr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fr-modal-handle" />
        <div className="fr-modal-header">
          <h3 className="fr-modal-title">Update Leave</h3>
          <button className="fr-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="fr-modal-body">
          <div className="fr-modal-field">
            <label className="fr-modal-label">Employee</label>
            <div className="fr-modal-value">{item[1]}</div>
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Type</label>
            <div className="fr-modal-value">{item[6]}</div>
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Pay Status</label>
            <div className="fr-modal-value">{item[10]==="Once" ? "Monthly" : "Bi-Monthly"}</div>
          </div>

          {datesReadOnly ? (
            <>
              <div className="fr-modal-field">
                <label className="fr-modal-label">Request Date</label>
                <div className="fr-modal-value">{displayDate(requestDate)}</div>
              </div>
              <div className="fr-modal-field">
                <label className="fr-modal-label">Return Date</label>
                <div className="fr-modal-value">{displayDate(returnDate)}</div>
              </div>
              <div className="fr-modal-field" style={{ fontSize: '0.78rem', color: 'rgba(251,191,36,0.85)', marginTop: '-4px' }}>
                Dates are read-only — record is from a prior month.
              </div>
            </>
          ) : (
            <>
              <div className="fr-modal-field">
                <label className="fr-modal-label">Request Date</label>
                <input
                  type="date"
                  className="fr-modal-input"
                  value={requestDate}
                  onChange={(e) => handleRequestDateChange(e.target.value)}
                />
              </div>
              <div className="fr-modal-field">
                <label className="fr-modal-label">Return Date</label>
                <input
                  type="date"
                  className="fr-modal-input"
                  value={returnDate}
                  onChange={(e) => handleReturnDateChange(e.target.value)}
                />
              </div>
            </>
          )}

          {/* Type Of Leave — editable for everyone */}
          <CustomSelect
            label="Leave"
            value={leaveType}
            onChange={handleLeaveTypeChange}
            options={typeLeaves}
          />

          {/* Time Worked — only for Sick / Carers leave, decimal hours */}
          {showTimeWorked && (
            <div className="fr-modal-field">
              <label className="fr-modal-label">Time Worked (hours)</label>
              <input
                type="text"
                inputMode="decimal"
                className="fr-modal-input"
                value={timeWorked}
                onChange={handleTimeWorkedChange}
                placeholder="0"
              />
              <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>
                Hours actually worked on this day — numbers only, e.g. 2.5
              </span>
            </div>
          )}

          {/* Status — admin only */}
          {isAdmin ? (
            <CustomSelect
              label="Status"
              value={statusApproved}
              onChange={setStatusApproved}
              options={leaveApproved}
            />
          ) : (
            <div className="fr-modal-field">
              <label className="fr-modal-label">Status</label>
              <div className="fr-modal-value">{item[5]}</div>
            </div>
          )}

          <div className="fr-modal-field">
          <label className="fr-modal-label">Supporting Documents</label>
          <div
            className={`fr-dropzone ${isDragging ? 'dragging' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-upload').click()}
          >
            <div className="fr-dropzone-content">
              <UploadIcon style={{ color: '#22d3ee', marginBottom: '8px' }} />
              <p className="fr-modal-value" style={{ fontSize: '0.9rem' }}>
                {selectedFile ? selectedFile.name : "Click or drag file to upload"}
              </p>
              <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>
                PDF, PNG, or JPG (max. 10MB)
              </span>
            </div>
            <input
              id="file-upload"
              type="file"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              style={{ display: 'none' }}
            />
          </div>
        </div>
        </div>

        <div className="fr-modal-actions">
          <button className="fr-modal-btn fr-btn-cancel" onClick={onClose}>
            Close
          </button>
          <button
            className="fr-modal-btn fr-btn-submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Update Leave'}
          </button>
        </div>
      </div>
    </div>
  );
};

const filterChipGroups = [
  {
    color: CHIP_GROUP_COLORS.cyan,
    values: ['ABN', 'TFN'],
  },
  {
    color: CHIP_GROUP_COLORS.violet,
    values: ['Holidays', 'Sick Leave', 'Other Leave', 'Termination'],
  },
  {
    color: CHIP_GROUP_COLORS.amber,
    values: ['Approved', 'Not Approved', 'Approved Without Pay', 'Cancelled', 'Pending'],
  },
  {
    color: CHIP_GROUP_COLORS.rose,
    values: ['Certificate', 'No Certificate'],
  },
];


const FilterLeave = ({ showHeader = false }) => {
  const { user } = useUser();
  const isAdmin = rolesAccess.admin.includes(user?.userJob || '');
  const isManagement = rolesAccess.management.includes(user?.userJob || '');

  return (
    <FilterTemplate
      title="Leave Entitlements"
      helpButton={
      <HelpButton helpData={[
        {chip: 'leave_filter', urlFilter: '/static/pdffilters/LeaveGuideFilter.pdf' }
      ]} />
  }

      subtitle="Employee Leave"
      searchPlaceholder="Search by fields..."
      stylesheetHref={filterLeaveStyles}
      fetchData={fetchLeave}
      dataKey="leaves"
      searchFields={[1, 2, 4, 5, 6, 7, 8, 9]}
      onSubmit={handleLeaveSubmit}
      loadingMessage="Loading leave..."
      showNavigation={true}
      showHeader={showHeader}
      chipLogic="and"
      onDownload={(isAdmin || isManagement) && downloadRequestLeave}
      renderChips={(chipQuery, setChipQuery, chipsOpen, setChipsOpen) => (
            <FilterChips
              chipGroups={filterChipGroups}
              chipQuery={chipQuery}
              setChipQuery={setChipQuery}
              chipsOpen={chipsOpen}
              setChipsOpen={setChipsOpen}
            />
      )}
      renderCard={(item, onClick) => (
        <LeaveCard item={item} onClick={onClick} isAdmin={isAdmin}/>
      )}
      renderModal={(item, onClose, onSubmit, isSubmitting, setToast) => (
         <LeaveModal
          item={item}
          onClose={onClose}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          onToast={setToast}
          isAdmin={isAdmin}
          user={user}
        />

      )}
    />
  );
};

export default FilterLeave;